import {
  arrayUnion,
  collection,
  doc,
  DocumentData,
  DocumentSnapshot,
  FirestoreError,
  onSnapshot,
  setDoc,
} from "firebase/firestore";

import { PhoneNumber } from "@/models/shared";
import { Log } from "@/models/log";
import { Collections } from "@/constants/firebase.collections";
import { currentTimeInSeconds } from "@/utils/common";

import { db } from "..";

// Logs Collection Ref
const logsRef = collection(db, Collections.LOGS);

export const setLogDoc = async (id: PhoneNumber, data: Log) => {
  const logDoc = doc(logsRef, id);

  if (data.type === "started_game") {
    await setDoc(logDoc, { started_game: data }, { merge: true });
  }

  if (data.type === "completed_game") {
    await setDoc(logDoc, { completed_game: data }, { merge: true });
  }

  if (data.type === "finished_outro_audio") {
    await setDoc(logDoc, { finished_outro_audio: data }, { merge: true });
  }

  if (data.type === "rated_game") {
    await setDoc(logDoc, { rated_game: data }, { merge: true });
  }

  if (data.type === "email_submitted") {
    await setDoc(logDoc, { email_submitted: data }, { merge: true });
  }

  if (data.type === "solved_stage") {
    await setDoc(logDoc, { solved_stages: arrayUnion(data) }, { merge: true });
  }

  if (data.type === "failed_stage") {
    await setDoc(logDoc, { failed_stages: arrayUnion(data) }, { merge: true });
  }

  // Help page logging
  if (data.type === "help_page_first_opened") {
    await setDoc(logDoc, { help_page_first_opened: data }, { merge: true });
  }

  if (data.type === "help_page_envelope_tab_click") {
    await setDoc(logDoc, { help_page_envelope_tab_clicks: arrayUnion(data) }, { merge: true });
  }

  if (data.type === "help_page_item_interaction") {
    await setDoc(logDoc, { help_page_item_interactions: arrayUnion(data) }, { merge: true });
  }

  if (data.type === "help_page_hint_interaction") {
    await setDoc(logDoc, { help_page_hint_interactions: arrayUnion(data) }, { merge: true });
  }

  if (data.type === "help_page_download") {
    await setDoc(logDoc, { help_page_downloads: arrayUnion(data) }, { merge: true });
  }

  if (data.type === "help_page_external_link_click") {
    await setDoc(logDoc, { help_page_external_link_clicks: arrayUnion(data) }, { merge: true });
  }
};

export const getLogDocOnSnapshot = (
  phone_number: PhoneNumber,
  onNext: (snapshot: DocumentSnapshot<DocumentData>) => void,
  onError?: (error: FirestoreError) => void
) => {
  const logDoc = doc(logsRef, phone_number);

  return onSnapshot(logDoc, onNext, onError);
};

// Help Page Logging Functions
export const logHelpPageFirstOpened = async (
  phoneNumber: PhoneNumber,
  difficulty: "easy" | "medium" | "hard"
) => {
  await setLogDoc(phoneNumber, {
    type: "help_page_first_opened",
    timestamp: currentTimeInSeconds(),
    difficulty,
  });
};

export const logHelpPageEnvelopeTabClick = async (
  phoneNumber: PhoneNumber,
  envelope: 1 | 2
) => {
  await setLogDoc(phoneNumber, {
    type: "help_page_envelope_tab_click",
    timestamp: currentTimeInSeconds(),
    envelope,
  });
};

export const logHelpPageItemInteraction = async (
  phoneNumber: PhoneNumber,
  itemId: string,
  itemType: "material" | "digital" | "question",
  itemTitle: string,
  envelope: 1 | 2 | "1 & 2"
) => {
  await setLogDoc(phoneNumber, {
    type: "help_page_item_interaction",
    timestamp: currentTimeInSeconds(),
    item_id: itemId,
    item_type: itemType,
    item_title: itemTitle,
    envelope,
  });
};

export const logHelpPageHintInteraction = async (
  phoneNumber: PhoneNumber,
  itemId: string,
  hintIndex: number
) => {
  await setLogDoc(phoneNumber, {
    type: "help_page_hint_interaction",
    timestamp: currentTimeInSeconds(),
    item_id: itemId,
    hint_index: hintIndex,
  });
};

export const logHelpPageDownload = async (
  phoneNumber: PhoneNumber,
  itemId: string,
  assetName: string,
  assetUrl: string
) => {
  await setLogDoc(phoneNumber, {
    type: "help_page_download",
    timestamp: currentTimeInSeconds(),
    item_id: itemId,
    asset_name: assetName,
    asset_url: assetUrl,
  });
};

export const logHelpPageExternalLinkClick = async (
  phoneNumber: PhoneNumber,
  itemId: string,
  linkUrl: string
) => {
  await setLogDoc(phoneNumber, {
    type: "help_page_external_link_click",
    timestamp: currentTimeInSeconds(),
    item_id: itemId,
    link_url: linkUrl,
  });
};
